import { BREAKPOINTS } from "../config/breakpoints";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Icons from "./SvgIcons";

const Card = styled.div`
	width: 100%;
	margin: 1rem 0;
	background: #ffffff;
	border-radius: 12px;
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
	overflow: hidden;
`;

const CardHeader = styled.div`
	background-color: #f8f9fa;
	padding: 15px 20px;
	border-bottom: 1px solid #e0e0e0;
`;

const CardTitle = styled.div`
	display: flex;
	align-items: center;
	font-size: 1.2rem;
	font-weight: bold;
	color: #333;
	margin: 0;

	svg {
		margin-right: 10px;
		color: rgba(45, 35, 66, 0.8);
	}
`;

const CardContent = styled.div`
	padding: 20px;
`;

const ActiveFilters = styled.div`
	background-color: #f0f8ff;
	padding: 10px;
	border-radius: 8px;
	margin-bottom: 15px;
	border: 1px solid #d1e8ff;
`;

const ActiveFiltersText = styled.div`
	font-size: 0.9rem;
	margin: 0 0 8px 0;
	color: rgba(45, 35, 66, 0.8);
`;

const Badge = styled.div`
	display: inline-flex;
	align-items: center;
	background-color: #e6f2ff;
	color: rgba(45, 35, 66, 0.8);
	padding: 3px 8px;
	border-radius: 12px;
	font-size: 0.8rem;
	margin-right: 6px;
	margin-bottom: 6px;
	border: 1px solid #b3d7ff;
`;

const BadgeButton = styled.div`
	background: none;
	border: none;
	cursor: pointer;
	padding: 0;
	margin-left: 6px;
	color: rgba(45, 35, 66, 0.8);
	&:hover {
		color: #0051cc;
	}
`;

const FilterGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: 15px;

	@media (min-width: ${BREAKPOINTS.tablet}px) {
		grid-template-columns: 1fr 1fr;
	}
`;

const FilterItem = styled.div`
	display: flex;
	flex-direction: column;
`;

const FilterLabel = styled.label`
	font-size: 0.8rem;
	font-weight: 600;
	color: #666;
	margin-bottom: 4px;
`;

const InputWrapper = styled.div`
	position: relative;
	display: flex;
`;

const Input = styled.input`
	flex-grow: 1;
	padding: 6px 10px 6px 30px;
	border: 1px solid #ccc;
	border-radius: 4px;
	font-size: 0.9rem;

	&:focus {
		outline: none;
		border-color: rgba(45, 35, 66, 0.8);
		box-shadow: 0 0 0 2px rgba(0, 112, 243, 0.2);
	}
`;

const InputIcon = styled.span`
	position: absolute;
	left: 8px;
	top: 50%;
	transform: translateY(-50%);
	color: #666;
	width: 16px;
	height: 16px;
`;

const AddButton = styled.button`
	background-color: rgba(45, 35, 66, 0.8);
	color: white;
	border: none;
	padding: 6px 12px;
	border-radius: 4px;
	font-size: 0.8rem;
	cursor: pointer;
	transition: background-color 0.2s;
	margin-left: 8px;
	display: flex;
	align-items: center;

	&:hover {
		background-color: #0051cc;
	}

	svg {
		margin-right: 4px;
		width: 14px;
		height: 14px;
	}
`;

const AddFilters = (props) => {

	const { filterTypes, onUpdate, initialFilters } = props;

	const [filters, setFilters] = useState(initialFilters || []);
	const [filterValues, setFilterValues] = useState({});
	const firstRender = useRef(true);

	const handleInputChange = (key, value) => {
		setFilterValues((prev) => ({ ...prev, [key]: value }));
	};

	const addOrUpdateFilter = (key) => {
		if (filterValues?.[key]) {
			const existingFilterIndex = filters.findIndex((filter) => filter.type === key);
			if (existingFilterIndex !== -1) {
				const updatedFilters = [...filters];
				updatedFilters[existingFilterIndex] = { type: key, value: filterValues[key] };
				setFilters(updatedFilters);
			} else {
				setFilters([...filters, { type: key, value: filterValues[key] }]);
			}
			setFilterValues((prev) => ({ ...prev, [key]: "" }));
		}
	};

	const removeFilter = (index) => {
		const newFilters = filters.filter((_, i) => i !== index);
		setFilters(newFilters);
	};

	useEffect(() => {
		if (firstRender.current) {
			firstRender.current = false;
			if (!initialFilters?.length) return;
			if (onUpdate) onUpdate(filters, true);
			return;
		}
		if (onUpdate) onUpdate(filters);
	}, [filters]);

	return (
		<>
			<Card>
				<CardHeader>
					<CardTitle>
						{Icons.filter}
						<span>Add Filters</span>
					</CardTitle>
				</CardHeader>
				<CardContent>
					{filters.length > 0 && (
						<ActiveFilters>
							<ActiveFiltersText>Active Filters</ActiveFiltersText>
							<div>
								{filters.map((filter, index) => (
									<Badge>
										{filterTypes.find((f) => f.key === filter.type)?.label}: {filter.value}
										<BadgeButton onClick={() => removeFilter(index)}>{Icons.x}</BadgeButton>
									</Badge>
								))}
							</div>
						</ActiveFilters>
					)}
					<FilterGrid>
						{filterTypes?.length ? filterTypes.map((filterType) => (
							<FilterItem key={filterType.key}>
								<FilterLabel htmlFor={filterType.key}>
									{filterType.label}
								</FilterLabel>
								<InputWrapper>
									<InputIcon>{filterType.icon}</InputIcon>
									<Input
										id={filterType.key}
										type={filterType.type}
										placeholder={`Enter ${filterType.label}`}
										value={filterValues[filterType.key]}
										onChange={(e) => handleInputChange(filterType.key, e.target.value)}
									/>
									<AddButton onClick={() => addOrUpdateFilter(filterType.key)} >
										{Icons.plus}
										{filters.some((f) => f.type === filterType.key) ? "Update" : "Add"}
									</AddButton>
								</InputWrapper>
							</FilterItem>
						)) : "No Filters found"}
					</FilterGrid>
				</CardContent>
			</Card>
		</>
	);
};

export default AddFilters;
